import { useEffect, useRef, useState } from 'react';

// Preferência de tema salva localmente — sobrevive a reload/nova visita,
// sem depender de login/backend. Compartilhado entre App.js (usuário
// logado) e Auth.js (tela de login) — os dois precisam do MESMO tema,
// senão a tela de login mostra o wrapper "light" (fundo claro fixo) por
// cima de um tema dark de verdade, com texto claro ilegível.
const THEME_STORAGE_KEY = 'ae-theme';
const COLOR_MODE_STORAGE_KEY = 'ae-color-mode';

const getStoredTheme = () => {
    try {
        return localStorage.getItem(THEME_STORAGE_KEY) || 'ae-light-mdc';
    } catch {
        return 'ae-light-mdc';
    }
};

export const getStoredColorMode = () => {
    try {
        return localStorage.getItem(COLOR_MODE_STORAGE_KEY) || 'light';
    } catch {
        return 'light';
    }
};

const isIE = () => /(MSIE|Trident\/|Edge\/)/i.test(window.navigator.userAgent);

// Gráficos (Chart.js) leem cores do tema via getComputedStyle e guardam o
// resultado num useMemo — não são notificados quando o CSS troca depois de
// já montados, então ficam com a cor antiga "presa". Esse evento dispara só
// depois do novo theme.css carregar de fato, pra quem escuta poder reler as
// variáveis com o valor já atualizado.
const THEME_CHANGE_EVENT = 'ae-theme-change';

// Troca o <link> clonando e só remove o antigo depois do novo carregar,
// pra não ter um "flash" sem nenhum tema aplicado entre a troca.
const replaceThemeLink = (href, callback) => {
    const linkElement = document.getElementById('theme-link');
    if (isIE()) {
        linkElement.setAttribute('href', href);
        if (callback) callback();
        window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
        return;
    }
    const id = linkElement.getAttribute('id');
    const cloneLinkElement = linkElement.cloneNode(true);
    cloneLinkElement.setAttribute('href', href);
    cloneLinkElement.setAttribute('id', id + '-clone');
    linkElement.parentNode.insertBefore(cloneLinkElement, linkElement.nextSibling);
    cloneLinkElement.addEventListener('load', () => {
        linkElement.remove();
        cloneLinkElement.setAttribute('id', id);
        if (callback) callback();
        window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
    });
};

// Hook separado (não faz parte do estado do tema em si) pra qualquer
// componente que precise recalcular algo baseado em CSS var() quando o
// tema muda — hoje usado só pelos gráficos.
export const useThemeVersion = () => {
    const [version, setVersion] = useState(0);
    useEffect(() => {
        const onChange = () => setVersion(v => v + 1);
        window.addEventListener(THEME_CHANGE_EVENT, onChange);
        return () => window.removeEventListener(THEME_CHANGE_EVENT, onChange);
    }, []);
    return version;
};

export const useTheme = () => {
    const [layoutColorMode, setLayoutColorMode] = useState(getStoredColorMode());
    const [theme, setTheme] = useState(getStoredTheme());

    // O <link> inicial (index.html) já aplica esse mesmo tema salvo antes do
    // React montar — troca o <link> só a partir da 2ª vez (mudança real),
    // senão recarregaria a folha de estilo à toa em toda visita.
    const isFirstThemeRender = useRef(true);
    useEffect(() => {
        if (isFirstThemeRender.current) {
            isFirstThemeRender.current = false;
        } else {
            replaceThemeLink('assets/themes/' + theme + '/theme.css');
        }
        try {
            localStorage.setItem(THEME_STORAGE_KEY, theme);
            localStorage.setItem(COLOR_MODE_STORAGE_KEY, layoutColorMode);
        } catch {
            // localStorage indisponível (modo privado/quota) — só não persiste,
            // não impede o tema de ser aplicado nesta sessão.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [theme]);

    // Usado tanto pelo seletor completo do AppConfig (dev/staging) quanto
    // pelo botão simples do topbar — os dois escrevem no mesmo estado/
    // localStorage, então a escolha feita em um se reflete no outro.
    const changeTheme = (newTheme, scheme) => {
        setLayoutColorMode(scheme);
        setTheme(newTheme);
    };

    // Alterna claro/escuro mantendo a mesma família de tema (ae-light <->
    // ae-dark, ae-light-mdc <-> ae-dark-mdc etc.) — não força de volta pro
    // Academia se o usuário tiver escolhido outro tema no seletor completo.
    const onToggleColorScheme = () => {
        const nextScheme = layoutColorMode === 'light' ? 'dark' : 'light';
        const nextTheme = layoutColorMode === 'light' ? theme.replace('light', 'dark') : theme.replace('dark', 'light');
        changeTheme(nextTheme, nextScheme);
    };

    return { theme, layoutColorMode, onColorModeChange: setLayoutColorMode, changeTheme, onToggleColorScheme };
};
